import type { Contact } from '../../../domain/contact';
import type { CollectedDataItem, EventDataChanged } from '../../../domain/message';
import type { Commands } from '../../../messageHandler';
import ScrappingMonitoringService from '../../scrapingMonitoring/scrappingMonitoringService';

type CRMMessageHandlerParams = {
  isCompanyPage: (url: URL | undefined) => boolean;
  isContactPage: (url: URL | undefined) => boolean;
  isLeadPage?: (url: URL | undefined) => boolean;
  extractionService: {
    extractCompanyDomain: (collectedData: CollectedDataItem[]) => string | undefined;
    extractContactInfo: (collectedData: CollectedDataItem[]) => Partial<Contact>;
    extractLeadInfo?: (collectedData: CollectedDataItem[]) => Partial<Contact>;
  };
  onCustomDataChange?: (commands: Commands, event: EventDataChanged, url: URL | undefined) => void;
};

export const createCRMMessageHandler = ({
  isCompanyPage,
  isContactPage,
  isLeadPage,
  extractionService,
  onCustomDataChange,
}: CRMMessageHandlerParams) => {
  const handleIframeLoad = (commands: Commands, url: URL | undefined): void => {
    if (isCompanyPage(url)) {
      commands.setCompanyPage();
      return;
    }
    if (isContactPage(url)) {
      commands.setContactPage();
      return;
    }

    if (isLeadPage?.(url)) {
      commands.setContactPage();
      return;
    }
    commands.setHomePage();
  };

  const handleDataChange = (commands: Commands, event: EventDataChanged, url: URL | undefined): void => {
    if (isCompanyPage(url)) {
      const companyDomain = extractionService.extractCompanyDomain(event.data);
      companyDomain && commands.setCompanyDomain(companyDomain);
      commands.setCompanyPage({ companyDomain });

      if (commands.getState().isOpen) {
        ScrappingMonitoringService.dispatchScrappingEvent({ data: companyDomain, url });
      }
    }

    if (isContactPage(url)) {
      const contactInfo = extractionService.extractContactInfo(event.data);
      const companyDomain = contactInfo.site;

      commands.setContactPage();
      companyDomain && commands.setCompanyDomain(companyDomain);
      commands.setCollectedContactInfo(contactInfo);

      if (commands.getState().isOpen) {
        ScrappingMonitoringService.dispatchScrappingEvent({ data: contactInfo, url });
      }
    }

    if (isLeadPage?.(url)) {
      const leadInfo = extractionService.extractLeadInfo?.(event.data);
      const companyDomain = leadInfo?.site;

      commands.setContactPage();
      companyDomain && commands.setCompanyDomain(companyDomain);
      if (leadInfo) {
        commands.setCollectedContactInfo(leadInfo);
      }

      if (commands.getState().isOpen) {
        ScrappingMonitoringService.dispatchScrappingEvent({ data: leadInfo, url });
      }
    }

    if (onCustomDataChange) {
      onCustomDataChange(commands, event, url);
    }
  };

  return {
    handleIframeLoad,
    handleDataChange,
  };
};
